import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User as SupabaseUser, Session } from '@supabase/supabase-js';

interface User {
  id: string;
  phone?: string;
  email?: string;
  fullName?: string;
  roles: ('customer' | 'filmmaker' | 'admin')[];
  locationId?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  session: Session | null;
  phone: string | null;
  loading: boolean;
  registerCustomer: (phone: string, fullName: string) => Promise<{ success: boolean; userId?: string; error?: string }>;
  sendVerificationCode: (phone: string) => Promise<{ success: boolean; devCode?: string; error?: string }>;
  verifyCodeAndLogin: (phone: string, code: string) => Promise<{ success: boolean; error?: string }>;
  sendSupabaseOTP: (phone: string) => Promise<{ success: boolean; devCode?: string; error?: string }>;
  verifySupabaseOTP: (phone: string, token: string) => Promise<{ success: boolean; error?: string }>;
  signInWithEmailPassword: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signUpWithEmailPassword: (email: string, password: string, phone: string, fullName: string) => Promise<{ success: boolean; error?: string }>;
  updateEmail: (newEmail: string) => Promise<{ success: boolean; error?: string }>;
  updatePassword: (newPassword: string) => Promise<{ success: boolean; error?: string }>;
  updateEmailAndPassword: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  updatePasswordWithToken: (newPassword: string) => Promise<{ success: boolean; error?: string }>;
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [phone, setPhone] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state from Supabase session
  useEffect(() => {
    // Check for temporary auth session from booking (localStorage)
    const loadTempAuth = () => {
      const tempAuthStr = localStorage.getItem('auth_session');
      if (tempAuthStr && !session) {
        try {
          const tempAuth = JSON.parse(tempAuthStr);
          if (tempAuth.user && tempAuth.verified) {
            setUser(tempAuth.user);
            setPhone(tempAuth.phone);
            setIsAuthenticated(true);
            setLoading(false);
            // Keep it so dashboard works - will be replaced by real session if user logs in
          }
        } catch (e) {
        }
      }
    };

    loadTempAuth();

    // Also listen for storage events (in case auth is updated in another tab/component)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth_session' && e.newValue && !session) {
        loadTempAuth();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        
        // Handle TOKEN_REFRESHED event - keep user authenticated
        if (event === 'TOKEN_REFRESHED' && session?.user) {
          // Token was refreshed, update session but keep user authenticated
          // Don't clear user state, just update session and ensure authenticated
          setIsAuthenticated(true);
          setLoading(false);
          // Update phone if available
          if (session.user.phone) {
            setPhone(session.user.phone);
          }
          return; // Don't process further, user stays authenticated
        }
        
        if (session?.user) {
          // Fetch user profile and roles
          fetchUserProfile(session.user).then(profile => {
            setUser(profile);
            setPhone(session.user.phone || null);
            setIsAuthenticated(true);
            setLoading(false);
            // Clear temp auth if real session established
            if (localStorage.getItem('auth_session')) {
              const tempAuth = JSON.parse(localStorage.getItem('auth_session') || '{}');
              if (tempAuth.verified && !tempAuth.user?.roles?.includes('admin')) {
                localStorage.removeItem('auth_session');
              }
            }
          });
        } else {
          // Only clear if event is SIGNED_OUT
          // Don't clear on TOKEN_REFRESHED or other events
          if (event === 'SIGNED_OUT') {
            // Always clear auth state on sign out, regardless of temp auth
            setUser(null);
            setPhone(null);
            setIsAuthenticated(false);
            setLoading(false);
            // Also clear temp auth on explicit logout
            localStorage.removeItem('auth_session');
          }
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        fetchUserProfile(session.user).then(profile => {
          setUser(profile);
          setPhone(session.user.phone || null);
          setIsAuthenticated(true);
          setSession(session);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []); // Run only once on mount - onAuthStateChange handles session updates

  // Helper function to fetch user profile data
  const fetchUserProfile = async (supabaseUser: SupabaseUser): Promise<User> => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', supabaseUser.id)
        .maybeSingle();

      const { data: userRoles } = await supabase
        .from('user_roles')
        .select('role, location_id')
        .eq('user_id', supabaseUser.id);

      const roles = (userRoles?.map(r => r.role) || ['customer']) as ('customer' | 'filmmaker' | 'admin')[];
      const locationId = userRoles?.find(r => r.location_id)?.location_id;

      // Prioritize auth.user.email over profile.email (auth is source of truth for email)
      // But if auth.user.email is missing or is a temp email, use profile.email
      const authEmail = supabaseUser.email;
      const isTempEmail = authEmail?.includes('@phone.temp') || authEmail?.includes('phone.temp');
      const userEmail = (authEmail && !isTempEmail) ? authEmail : (profile?.email || authEmail || '');
      
      return {
        id: supabaseUser.id,
        phone: supabaseUser.phone || profile?.phone || '',
        email: userEmail,
        fullName: profile?.full_name || 'User',
        roles,
        locationId,
      };
    } catch (error) {
      return {
        id: supabaseUser.id,
        phone: supabaseUser.phone || '',
        email: supabaseUser.email || '',
        fullName: 'User',
        roles: ['customer'],
      };
    }
  };

  // Register new customer (checks for existing profile/user - profile will be created when auth.user is created)
  // IMPORTANT: profiles.id MUST equal auth.users.id (foreign key constraint)
  // So we CANNOT create profiles before auth.users exist
  const registerCustomer = async (phone: string, fullName: string) => {
    try {
      // Check if profile already exists
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('phone', phone)
        .maybeSingle();

      if (existingProfile) {
        return { success: true, userId: existingProfile.id }; // Already registered
      }

      // Check if auth user exists (but profile might not exist yet)
      // We can't create profile without auth.user due to foreign key constraint
      // Profile will be created automatically by handle_new_user() trigger when auth.user is created via OTP
      
      // For now, just return success - the actual user/profile will be created during OTP verification
      // This is used in booking flow, and the profile will be created during payment when auth.user is created
      return { success: true, userId: null }; // Will be set after auth.user creation
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to register' };
    }
  };

  // @deprecated - Use sendSupabaseOTP instead. This function is kept for backward compatibility only.
  // Send verification code via SMS (Vonage edge function - old method)
  const sendVerificationCode = async (phone: string) => {
    try {
      // Send real SMS via Vonage
      const { data, error } = await supabase.functions.invoke('send-sms', {
        body: { phone },
      });


      if (error) {
        throw error;
      }

      // Check if in dev mode (Vonage failed but code was generated)
      if (data?.devMode && data?.devCode) {
        return { success: true, devCode: data.devCode };
      }

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to send code' };
    }
  };

  // @deprecated - Use verifySupabaseOTP instead. This function is kept for backward compatibility only.
  // Verify code and create/login auth user (Vonage edge function - old method)
  const verifyCodeAndLogin = async (phone: string, code: string) => {
    try {
      // Verify SMS code via edge function
      const { data: verifyData, error: verifyError } = await supabase.functions.invoke('verify-sms', {
        body: { phone, code },
      });


      if (verifyError || !verifyData?.success) {
        return { 
          success: false, 
          error: verifyData?.message || 'Invalid or expired code' 
        };
      }

      // Code verified via Vonage! 

      // Use userId from verify-sms response (more reliable than searching by phone)
      const userId = verifyData?.userId;

      // In booking flow, userId might be null if user doesn't have profile yet
      // Profile will be created during payment - that's OK!
      if (!userId) {
        
        // Store phone verification - profile will be created during payment
        localStorage.setItem('sms_verified', JSON.stringify({
          phone,
          verifiedAt: new Date().toISOString(),
          isBookingFlow: true // Flag to indicate this is booking flow
        }));

        // Set minimal auth state for booking flow
        // User will get full profile after payment
        setPhone(phone);
        setIsAuthenticated(true); // Allow proceeding to payment

        return { success: true };
      }


      // Load profile using edge function with JWT (respects RLS)
      // Note: This is after SMS verification, user might not have JWT yet
      // So we'll use direct invoke but the function will handle it
      const { data: profileData, error: profileError } = await supabase.functions.invoke('get-user-profile', {
        body: { userId }
      });

      if (profileError || !profileData?.profile) {
        return { 
          success: false, 
          error: 'Profile not found. Please complete a booking first.' 
        };
      }

      const profile = profileData.profile;
      const roles = (profileData.roles?.map((r: any) => r.role) || ['customer']) as ('customer' | 'filmmaker' | 'admin')[];
      const locationId = profileData.roles?.find((r: any) => r.location_id)?.location_id;


      // CRITICAL: Establish Supabase Auth session
      // Since SMS was verified externally (Vonage), we need to establish Supabase Auth session
      // The proper way is signInWithOtp, which will send another SMS (unfortunate but required)
      try {
        
        // Check if there's already a session
        const { data: { session: existingSession } } = await supabase.auth.getSession();
        
        if (!existingSession) {
          
          // Send OTP via Supabase Auth (this will send another SMS)
          // User already verified SMS externally, but Supabase needs its own verification
          const { data: otpData, error: otpError } = await supabase.auth.signInWithOtp({
            phone: phone,
            options: {
              shouldCreateUser: true,
            }
          });
          
          if (!otpError) {
          } else {
          }
        } else {
          setSession(existingSession);
        }
      } catch (authError) {
      }

      // Set user state
      const verifiedUser: User = {
        id: profile.id,
        phone: profile.phone || phone,
        email: profile.email || '',
        fullName: profile.full_name || 'User',
        roles,
        locationId,
      };

      setUser(verifiedUser);
      setPhone(phone);
      setIsAuthenticated(true);

      // Store verification status and auth session
      localStorage.setItem('sms_verified', JSON.stringify({
        phone,
        userId: profile.id,
        verifiedAt: new Date().toISOString()
      }));

      // Also store in auth_session for compatibility
      localStorage.setItem('auth_session', JSON.stringify({
        user: verifiedUser,
        phone: profile.phone || phone,
        verified: true,
        userId: profile.id
      }));

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to verify code' };
    }
  };

  // Normalize phone number to E.164 format (only digits and +)
  const normalizePhoneToE164 = (phone: string): string => {
    // Remove all non-digit characters except the leading +
    return phone.replace(/[^\d+]/g, '');
  };

  // Send OTP using Supabase Phone OTP (native method)
  const sendSupabaseOTP = async (phone: string) => {
    try {
      // Normalize phone to E.164 format before sending
      const normalizedPhone = normalizePhoneToE164(phone);

      // Use Supabase native Phone OTP
      const { data, error } = await supabase.auth.signInWithOtp({
        phone: normalizedPhone,
      });

      if (error) {
        return { success: false, error: error.message || 'Failed to send OTP' };
      }

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to send OTP' };
    }
  };

  // Verify OTP using Supabase Phone OTP (native method)
  // This automatically establishes JWT session if successful
  const verifySupabaseOTP = async (phone: string, token: string) => {
    try {
      // Normalize phone to E.164 format before verifying
      const normalizedPhone = normalizePhoneToE164(phone);

      // Use Supabase native OTP verification
      // This automatically establishes JWT session if successful
      const { data, error } = await supabase.auth.verifyOtp({
        phone: normalizedPhone,
        token: token,
        type: 'sms',
      });

      if (error) {
        return { 
          success: false, 
          error: error.message || 'Invalid or expired code' 
        };
      }

      // Success! Session is automatically established by Supabase

      // Session is now set, so fetch user profile
      if (data.session?.user) {
        const profile = await fetchUserProfile(data.session.user);
        
        setUser(profile);
        setPhone(data.session.user.phone || phone);
        setSession(data.session);
        setIsAuthenticated(true);

        // Store in localStorage for compatibility
        localStorage.setItem('auth_session', JSON.stringify({
          user: profile,
          phone: data.session.user.phone || phone,
          verified: true,
          userId: profile.id
        }));
      }

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to verify OTP' };
    }
  };

  // Sign in with email and password
  const signInWithEmailPassword = async (email: string, password: string) => {
    try {
      // Use Supabase native email/password sign in
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { 
          success: false, 
          error: error.message || 'Invalid email or password' 
        };
      }

      // Success! Session is automatically established by Supabase

      // Session is now set, so fetch user profile
      if (data.session?.user) {
        const profile = await fetchUserProfile(data.session.user);
        
        setUser(profile);
        setPhone(data.session.user.phone || null);
        setSession(data.session);
        setIsAuthenticated(true);

        // Store in localStorage for compatibility
        localStorage.setItem('auth_session', JSON.stringify({
          user: profile,
          phone: data.session.user.phone || null,
          email: data.session.user.email || email,
          verified: true,
          userId: profile.id
        }));
      }

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to sign in' };
    }
  };

  // Sign up with email, password, phone, and full name
  const signUpWithEmailPassword = async (email: string, password: string, phone: string, fullName: string) => {
    try {
      // Normalize phone to E.164 format
      const normalizedPhone = normalizePhoneToE164(phone);

      // Use Supabase native sign up
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        phone: normalizedPhone,
        options: {
          data: {
            full_name: fullName,
            phone: normalizedPhone,
          },
          emailRedirectTo: undefined, // No email confirmation needed
        },
      });

      if (error) {
        return { 
          success: false, 
          error: error.message || 'Failed to create account' 
        };
      }

      // Success! Session is automatically established by Supabase

      // Session is now set, so fetch user profile
      if (data.session?.user) {
        const profile = await fetchUserProfile(data.session.user);
        
        setUser(profile);
        setPhone(data.session.user.phone || normalizedPhone);
        setSession(data.session);
        setIsAuthenticated(true);

        // Store in localStorage for compatibility
        localStorage.setItem('auth_session', JSON.stringify({
          user: profile,
          phone: data.session.user.phone || normalizedPhone,
          email: data.session.user.email || email,
          verified: true,
          userId: profile.id
        }));
      }

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to create account' };
    }
  };

  // Update user email
  const updateEmail = async (newEmail: string) => {
    try {
      // Check if user has email - if not, use edge function with Admin API
      const { data: { user: authUser } } = await supabase.auth.getUser();
      
      if (!authUser?.email || authUser.email.includes('@phone.temp')) {
        // User doesn't have email - use edge function with Admin API
        const { invokeFunctionWithAuth } = await import('@/lib/supabase/functions');
        
        const { data, error } = await invokeFunctionWithAuth('update-user-email-password', {
          email: newEmail,
        });

        if (error) {
          return { 
            success: false, 
            error: error.message || 'Failed to update email' 
          };
        }

        // Success! Refresh user profile to get updated email
        await refreshUser();

        return { success: true };
      }

      // User has email - use edge function to update both auth.user and profile
      // This ensures both are updated consistently and handles RLS properly
      const { invokeFunctionWithAuth } = await import('@/lib/supabase/functions');
      
      const { data: funcData, error: funcError } = await invokeFunctionWithAuth('update-user-email-password', {
        email: newEmail.trim(),
      });

      if (funcError) {
        return { 
          success: false, 
          error: funcError.message || 'Failed to update email' 
        };
      }

      // Success! Refresh user profile to get updated email
      await refreshUser();

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to update email' };
    }
  };

  // Update user password
  const updatePassword = async (newPassword: string) => {
    try {
      // Check if user has email - password requires email
      const { data: { user: authUser } } = await supabase.auth.getUser();
      
      if (!authUser?.email || authUser.email.includes('@phone.temp')) {
        // User doesn't have email - can't set password without email
        return { 
          success: false, 
          error: 'Email is required to set a password. Please set your email first.' 
        };
      }

      // User has email - use Supabase native password update
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        return { 
          success: false, 
          error: error.message || 'Failed to update password' 
        };
      }

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to update password' };
    }
  };

  // Update both email and password
  const updateEmailAndPassword = async (email: string, password: string) => {
    try {
      // Check if user has email - if not, use edge function with Admin API
      const { data: { user: authUser } } = await supabase.auth.getUser();
      
      if (!authUser?.email || authUser.email.includes('@phone.temp')) {
        // User doesn't have email - use edge function with Admin API
        const { invokeFunctionWithAuth } = await import('@/lib/supabase/functions');
        
        const { data, error } = await invokeFunctionWithAuth('update-user-email-password', {
          email,
          password,
        });

        if (error) {
          return { 
            success: false, 
            error: error.message || 'Failed to update email and password' 
          };
        }

        // Success! Refresh user profile to get updated email
        await refreshUser();

        return { success: true };
      }

      // User has email - use edge function to update both auth.user and profile
      // This ensures both are updated consistently and handles RLS properly
      const { invokeFunctionWithAuth } = await import('@/lib/supabase/functions');
      
      const { data: funcData, error: funcError } = await invokeFunctionWithAuth('update-user-email-password', {
        email: email.trim(),
        password,
      });

      if (funcError) {
        return { 
          success: false, 
          error: funcError.message || 'Failed to update email and password' 
        };
      }

      // Success! Refresh user profile to get updated email
      await refreshUser();

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to update email and password' };
    }
  };

  // Reset password - Send password reset email
  const resetPassword = async (email: string) => {
    try {
      // Get the base URL - use environment variable if available, otherwise use current origin
      // This allows production deployments (Vercel) to use the correct URL instead of localhost
      const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin;
      const redirectUrl = `${baseUrl}/reset-password`;
      

      // Use Supabase native password reset
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        return { 
          success: false, 
          error: error.message || 'Failed to send password reset email' 
        };
      }

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to send password reset email' };
    }
  };

  // Update password with token (from password reset email)
  const updatePasswordWithToken = async (newPassword: string) => {
    try {
      // Use Supabase native password update (requires valid session from password reset)
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        return { 
          success: false, 
          error: error.message || 'Failed to update password' 
        };
      }
      
      // Refresh user session
      await refreshUser();
      
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to update password' };
    }
  };

  // Admin email/password login
  const login = async (identifier: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: identifier,
      password,
    });

    if (error) throw error;
    // Auth state will be updated by onAuthStateChange listener
  };

  const refreshUser = async () => {
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (currentSession?.user) {
        const profile = await fetchUserProfile(currentSession.user);
        setUser(profile);
      }
    } catch (error) {
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setPhone(null);
    setSession(null);
    setIsAuthenticated(false);
    localStorage.removeItem('auth_session');
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      user, 
      session,
      phone, 
      loading,
      registerCustomer,
      sendVerificationCode,
      verifyCodeAndLogin,
      sendSupabaseOTP,
      verifySupabaseOTP,
      signInWithEmailPassword,
      signUpWithEmailPassword,
      updateEmail,
      updatePassword,
      updateEmailAndPassword,
      resetPassword,
      updatePasswordWithToken,
      login, 
      logout,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
